'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function TileCalculator() {
  const t = useTranslations("tools.tileCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    tileLength: 12,
    tileLengthUnit: 'inch',
    tileWidth: 12,
    tileWidthUnit: 'inch',
    areaType: 'dimensions',
    areaLength: 20,
    areaLengthUnit: 'foot',
    areaWidth: 15,
    areaWidthUnit: 'foot',
    totalArea: 300,
    totalAreaUnit: 'sqft',
    gapSize: 0.25,
    gapSizeUnit: 'inch',
    wastage: 10,
    boxSize: 12,
    price: 0,
    priceUnit: 'box',
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const conversionToInches: { [key: string]: number } = {
    inch: 1,
    foot: 12,
    cm: 0.393701,
    meter: 39.3701,
    mm: 0.0393701
  };

  const conversionToSqFt: { [key: string]: number } = {
    sqft: 1,
    sqm: 10.7639,
    sqin: 0.00694444
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const tileLengthInches = inputs.tileLength * conversionToInches[inputs.tileLengthUnit];
      const tileWidthInches = inputs.tileWidth * conversionToInches[inputs.tileWidthUnit];
      const gapSizeInches = inputs.gapSize * conversionToInches[inputs.gapSizeUnit];

      const effectiveTileLength = tileLengthInches + gapSizeInches;
      const effectiveTileWidth = tileWidthInches + gapSizeInches;
      const tileAreaSqIn = effectiveTileLength * effectiveTileWidth;
      const tileAreaSqFt = tileAreaSqIn / 144;

      let totalAreaSqFt;

      if (inputs.areaType === 'dimensions') {
        const areaLengthInches = inputs.areaLength * conversionToInches[inputs.areaLengthUnit];
        const areaWidthInches = inputs.areaWidth * conversionToInches[inputs.areaWidthUnit];
        totalAreaSqFt = (areaLengthInches * areaWidthInches) / 144;
      } else {
        totalAreaSqFt = inputs.totalArea * conversionToSqFt[inputs.totalAreaUnit];
      }

      const baseTilesNeeded = Math.ceil(totalAreaSqFt / tileAreaSqFt);
      const totalTilesNeeded = Math.ceil(baseTilesNeeded * (1 + inputs.wastage / 100));
      const boxesNeeded = Math.ceil(totalTilesNeeded / inputs.boxSize);

      let baseCost = 0;
      let wastageCost = 0;
      let totalCost = 0;

      if (inputs.price > 0) {
        if (inputs.priceUnit === 'tile') {
          baseCost = baseTilesNeeded * inputs.price;
        } else if (inputs.priceUnit === 'box') {
          baseCost = Math.ceil(baseTilesNeeded / inputs.boxSize) * inputs.price;
        } else if (inputs.priceUnit === 'sqft') {
          baseCost = totalAreaSqFt * inputs.price;
        }

        wastageCost = baseCost * (inputs.wastage / 100);
        totalCost = baseCost + wastageCost;
      }

      setResults({
        totalTiles: totalTilesNeeded,
        boxesNeeded,
        totalArea: totalAreaSqFt.toFixed(2),
        tileArea: tileAreaSqFt.toFixed(4),
        baseCost: baseCost.toFixed(2),
        wastageCost: wastageCost.toFixed(2),
        totalCost: totalCost.toFixed(2),
        showCost: inputs.price > 0,
      });

    } catch (err) {
      console.error(err);
      setError(t('errorMessage'));
    }
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
      borderRadius: '1rem 1rem 0 0',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const toolTitleStyle = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formSectionStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
      border: '2px solid #e2e8f0',
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const formSectionTitleStyle = getResponsiveStyle(
    { margin: '0 0 1rem 0', color: colors.textPrimary, fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      fontWeight: 700,
      color: '#fff',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      border: 'none',
      borderRadius: '0.75rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 15px rgba(37, 99, 235, 0.3)',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid #22c55e',
      borderRadius: '0 0 1rem 1rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={toolTitleStyle}>{t('toolTitle')}</h2>
        </div>

        <form onSubmit={handleCalculate} style={{ maxWidth: '900px', margin: '0 auto' }}>
          {error && (
            <div style={{
              background: '#fef2f2',
              border: '2px solid #fecaca',
              color: '#dc2626',
              padding: '1rem',
              borderRadius: '0.5rem',
              marginBottom: '1rem',
            }}>
              {error}
            </div>
          )}

          <div style={formSectionStyle}>
            <h3 style={formSectionTitleStyle}>📐 {t('tileSizeTitle')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('tileLengthLabel')}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={inputs.tileLength}
                    onChange={(e) => setInputs({...inputs, tileLength: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={inputs.tileLengthUnit}
                    onChange={(e) => setInputs({...inputs, tileLengthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t('unitInches')}</option>
                    <option value="foot">{t('unitFeet')}</option>
                    <option value="cm">{t('unitCm')}</option>
                    <option value="meter">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('tileWidthLabel')}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={inputs.tileWidth}
                    onChange={(e) => setInputs({...inputs, tileWidth: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={inputs.tileWidthUnit}
                    onChange={(e) => setInputs({...inputs, tileWidthUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t('unitInches')}</option>
                    <option value="foot">{t('unitFeet')}</option>
                    <option value="cm">{t('unitCm')}</option>
                    <option value="meter">{t('unitMeters')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div style={formSectionStyle}>
            <h3 style={formSectionTitleStyle}>📏 {t('areaToCoverTitle')}</h3>
            <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#475569', fontWeight: 500 }}>
                <input
                  type="radio"
                  name="area-type"
                  value="dimensions"
                  checked={inputs.areaType === 'dimensions'}
                  onChange={(e) => setInputs({...inputs, areaType: e.target.value})}
                  style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                />
                {t('useDimensions')}
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#475569', fontWeight: 500 }}>
                <input
                  type="radio"
                  name="area-type"
                  value="total"
                  checked={inputs.areaType === 'total'}
                  onChange={(e) => setInputs({...inputs, areaType: e.target.value})}
                  style={{ width: '1.25rem', height: '1.25rem', cursor: 'pointer' }}
                />
                {t('useTotalArea')}
              </label>
            </div>

            {inputs.areaType === 'dimensions' ? (
              <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                    {t('areaLengthLabel')}
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={inputs.areaLength}
                      onChange={(e) => setInputs({...inputs, areaLength: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      required
                    />
                    <select
                      value={inputs.areaLengthUnit}
                      onChange={(e) => setInputs({...inputs, areaLengthUnit: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="foot">{t('unitFeet')}</option>
                      <option value="inch">{t('unitInches')}</option>
                      <option value="meter">{t('unitMeters')}</option>
                      <option value="cm">{t('unitCm')}</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                    {t('areaWidthLabel')}
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={inputs.areaWidth}
                      onChange={(e) => setInputs({...inputs, areaWidth: parseFloat(e.target.value) || 0})}
                      style={inputStyle}
                      required
                    />
                    <select
                      value={inputs.areaWidthUnit}
                      onChange={(e) => setInputs({...inputs, areaWidthUnit: e.target.value})}
                      style={selectStyle}
                    >
                      <option value="foot">{t('unitFeet')}</option>
                      <option value="inch">{t('unitInches')}</option>
                      <option value="meter">{t('unitMeters')}</option>
                      <option value="cm">{t('unitCm')}</option>
                    </select>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('totalAreaLabel')}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={inputs.totalArea}
                    onChange={(e) => setInputs({...inputs, totalArea: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                    required
                  />
                  <select
                    value={inputs.totalAreaUnit}
                    onChange={(e) => setInputs({...inputs, totalAreaUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="sqft">{t('unitSqFt')}</option>
                    <option value="sqm">{t('unitSqM')}</option>
                    <option value="sqin">{t('unitSqIn')}</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          <div style={formSectionStyle}>
            <h3 style={formSectionTitleStyle}>⚙️ {t('additionalSettingsTitle')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('gapSizeLabel')}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={inputs.gapSize}
                    onChange={(e) => setInputs({...inputs, gapSize: parseFloat(e.target.value) || 0})}
                    style={inputStyle}
                  />
                  <select
                    value={inputs.gapSizeUnit}
                    onChange={(e) => setInputs({...inputs, gapSizeUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="inch">{t('unitInches')}</option>
                    <option value="cm">{t('unitCm')}</option>
                    <option value="mm">{t('unitMm')}</option>
                  </select>
                </div>
                <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {t('gapSizeHelper')}
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('wastageLabel')}
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={inputs.wastage}
                  onChange={(e) => setInputs({...inputs, wastage: parseFloat(e.target.value) || 0})}
                  style={inputStyle}
                />
                <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                  {t('wastageHelper')}
                </div>
              </div>
            </div>
          </div>

          <div style={formSectionStyle}>
            <h3 style={formSectionTitleStyle}>💰 {t('costEstimationTitle')}</h3>
            <div style={{ display: 'grid', gridTemplateColumns: screenSize === 'mobile' ? '1fr' : '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('boxSizeLabel')}
                </label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  value={inputs.boxSize}
                  onChange={(e) => setInputs({...inputs, boxSize: parseInt(e.target.value) || 1})}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>
                  {t('priceLabel')}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputs.price || ''}
                    onChange={(e) => setInputs({...inputs, price: parseFloat(e.target.value) || 0})}
                    placeholder="0.00"
                    style={inputStyle}
                  />
                  <select
                    value={inputs.priceUnit}
                    onChange={(e) => setInputs({...inputs, priceUnit: e.target.value})}
                    style={selectStyle}
                  >
                    <option value="tile">{t('pricePerTile')}</option>
                    <option value="box">{t('pricePerBox')}</option>
                    <option value="sqft">{t('pricePerSqFt')}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <button type="submit" style={calculateButtonStyle}>
            {t('calculateButton')}
          </button>
        </form>

        {results && (
          <div style={resultsStyle}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#166534', fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', textAlign: 'center' }}>
              📊 {t('resultsTitle')}
            </h3>

            <div style={{
              display: 'grid',
              gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                color: 'white',
                padding: '1.25rem',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
              }}>
                <span style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  {t('totalTilesLabel')}
                </span>
                <span style={{ display: 'block', color: 'white', fontSize: '2rem', fontWeight: 700 }}>
                  {results.totalTiles.toLocaleString()}
                </span>
              </div>

              <div style={{ background: 'white', padding: '1.25rem', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                <span style={{ display: 'block', color: '#64748b', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  {t('boxesNeededLabel')}
                </span>
                <span style={{ display: 'block', color: colors.textPrimary, fontSize: '1.5rem', fontWeight: 700 }}>
                  {results.boxesNeeded.toLocaleString()}
                </span>
              </div>

              <div style={{ background: 'white', padding: '1.25rem', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                <span style={{ display: 'block', color: '#64748b', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  {t('totalAreaLabel')}
                </span>
                <span style={{ display: 'block', color: colors.textPrimary, fontSize: '1.5rem', fontWeight: 700 }}>
                  {results.totalArea} {t('unitSqFt')}
                </span>
              </div>

              <div style={{ background: 'white', padding: '1.25rem', borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)' }}>
                <span style={{ display: 'block', color: '#64748b', fontSize: '0.875rem', fontWeight: 500, marginBottom: '0.5rem' }}>
                  {t('tileCoverageLabel')}
                </span>
                <span style={{ display: 'block', color: colors.textPrimary, fontSize: '1.5rem', fontWeight: 700 }}>
                  {results.tileArea} {t('unitSqFt')}
                </span>
              </div>
            </div>

            {results.showCost && (
              <div style={{ background: 'white', padding: '1.25rem', borderRadius: '0.75rem', marginTop: '1rem' }}>
                <h4 style={{ margin: '0 0 1rem 0', color: colors.textPrimary, fontSize: '1.125rem' }}>
                  💵 {t('costBreakdownTitle')}
                </h4>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span>{t('baseCostLabel')}</span>
                  <span>${results.baseCost}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' }}>
                  <span>{t('wastageCostLabel')}</span>
                  <span>${results.wastageCost}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', fontWeight: 700, color: '#2563eb', fontSize: '1.125rem' }}>
                  <span>{t('totalCostLabel')}</span>
                  <span>${results.totalCost}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep1')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep2')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep3')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep4')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep5')}</li>
          <li style={{ marginBottom: '0.75rem' }}>{t('howToStep6')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <div style={{ ...paragraphStyle, background: '#f0f9ff', padding: '1.5rem', borderRadius: '0.75rem', borderLeft: '4px solid #2563eb' }}>
          <p style={{ marginBottom: '1rem' }}>{t('insight1')}</p>
          <p style={{ marginBottom: '1rem' }}>{t('insight2')}</p>
          <p style={{ marginBottom: '1rem' }}>{t('insight3')}</p>
          <p style={{ marginBottom: 0 }}>{t('insight4')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsSubtitle1')}
        </h3>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsSubtitle2')}
        </h3>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsSubtitle3')}
        </h3>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('detailsSubtitle4')}
        </h3>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faqQuestion1')}
          </h3>
          <p style={paragraphStyle}>{t('faqAnswer1')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faqQuestion2')}
          </h3>
          <p style={paragraphStyle}>{t('faqAnswer2')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faqQuestion3')}
          </h3>
          <p style={paragraphStyle}>{t('faqAnswer3')}</p>
        </div>
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faqQuestion4')}
          </h3>
          <p style={paragraphStyle}>{t('faqAnswer4')}</p>
        </div>
        <div>
          <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginBottom: '0.5rem' }}>
            {t('faqQuestion5')}
          </h3>
          <p style={paragraphStyle}>{t('faqAnswer5')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={{ ...paragraphStyle, paddingLeft: '1.5rem' }}>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.elsner.com/tile-ecommerce-store-with-advanced-features/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference1')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://tileswale.com/blog-detail/tileswale-free-tile-calculator-quick-and-accurate-estimates" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference2')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.mtmtileshowroom.co.uk/post/expert-tiling-advice-and-online-tools" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference3')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://structuralconsultancy.com.au/tile-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference4')}
            </a>
          </li>
          <li style={{ marginBottom: '0.5rem' }}>
            <a href="https://www.porcelanosa.com/trendbook/uk/tile-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>
              {t('reference5')}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}